import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Phone, ChevronRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICE_DETAILS } from "@/servicesData";
import { SERVICES } from "@/data";
import AreasServed from "@/components/site/AreasServed";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

const BASE = typeof window !== "undefined" ? window.location.origin : "";

export default function ServicePage({ onNav, aliasSlug }) {
  const params = useParams();
  const slug = aliasSlug || params.slug;
  const detail = SERVICE_DETAILS[slug];

  if (!detail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display text-4xl text-[#f5f5f0]">Service not found</h1>
        <Link to="/" className="border border-[#c4a962] px-6 py-3 text-sm uppercase tracking-wide text-[#c4a962]">
          Back to home
        </Link>
      </div>
    );
  }

  const url = `${BASE}/services/${slug}`;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: detail.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: detail.name,
    provider: { "@type": "ProfessionalService", name: "FS Restoration Group", telephone: "+61407051043" },
    areaServed: ["Victoria", "New South Wales"],
    description: detail.metaDescription,
    url,
  };

  return (
    <div className="bg-[#050505]">
      {/* React 19 native metadata hoisting */}
      <title>{detail.metaTitle}</title>
      <meta name="description" content={detail.metaDescription} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={detail.metaTitle} />
      <meta property="og:description" content={detail.metaDescription} />
      <meta property="og:url" content={url} />
      <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#2a2a2a] px-5 pb-16 pt-40 md:px-10 md:pb-24 md:pt-52">
        <div className="mx-auto max-w-[1400px]">
          <nav className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8a8a8a]">
            <Link to="/" className="transition-colors hover:text-[#c4a962]">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <button onClick={() => onNav("#services")} className="transition-colors hover:text-[#c4a962]">Services</button>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#f5f5f0]">{detail.name}</span>
          </nav>
          <Eyebrow>IICRC Certified · Swan Hill, VIC & NSW</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 max-w-4xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter text-[#f5f5f0] sm:text-5xl lg:text-6xl"
          >
            {detail.name}
          </motion.h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#8a8a8a] md:text-lg">
            {detail.intro}
          </p>
          <button
            onClick={() => onNav("#contact")}
            data-testid="service-quote-btn"
            className="group mt-9 inline-flex items-center gap-3 bg-[#f5f5f0] px-6 py-4 text-sm font-semibold uppercase tracking-wide text-[#050505] transition-colors duration-300 hover:bg-[#c4a962]"
          >
            Request a quote
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </section>

      {/* Body */}
      <section className="px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-7">
            {detail.paragraphs.map((p, i) => (
              <Reveal key={i} i={i}>
                <p className="mb-6 text-lg leading-relaxed text-[#c9c9c2]">{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal i={1} className="md:col-span-5">
            <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-8">
              <h2 className="font-display text-xl font-medium tracking-tight text-[#f5f5f0]">
                What&apos;s involved
              </h2>
              <ul className="mt-6 flex flex-col gap-4">
                {detail.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-[#c9c9c2]">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#c4a962]" strokeWidth={2.4} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#2a2a2a] px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[900px]">
          <Eyebrow>Frequently asked</Eyebrow>
          <h2 className="mb-10 mt-4 font-display text-4xl font-semibold tracking-tighter text-[#f5f5f0] sm:text-5xl">
            {detail.name} — your questions
          </h2>
          <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
            {detail.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-[#2a2a2a]">
                <AccordionTrigger className="py-6 text-left font-display text-lg tracking-tight text-[#f5f5f0] hover:text-[#c4a962] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-base leading-relaxed text-[#8a8a8a]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <AreasServed serviceName={detail.name} />

      {/* Other services */}
      <section className="border-t border-[#2a2a2a] px-5 py-24 md:px-10 md:py-32">
        <div className="mx-auto max-w-[1400px]">
          <Eyebrow>More services</Eyebrow>
          <div className="mt-8 grid grid-cols-1 gap-px border border-[#2a2a2a] bg-[#2a2a2a] sm:grid-cols-2 lg:grid-cols-4">
            {others.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                data-testid={`related-${s.slug}`}
                className="group flex items-center justify-between gap-3 bg-[#050505] p-6 transition-colors duration-300 hover:bg-[#121212]"
              >
                <span className="font-display text-base tracking-tight text-[#f5f5f0]">{s.title}</span>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-[#8a8a8a] transition-colors group-hover:text-[#c4a962]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="border-t border-[#2a2a2a] bg-[#0a0a0a] px-5 py-20 md:px-10">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-3xl font-semibold tracking-tighter text-[#f5f5f0] sm:text-4xl">
              Need {detail.name.toLowerCase()}?
            </h2>
            <p className="mt-3 text-base text-[#8a8a8a]">We attend site, scope the works and provide a clear quote.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href="tel:0407051043" className="flex items-center justify-center gap-2 border border-[#2a2a2a] px-6 py-4 text-sm font-medium uppercase tracking-wide text-[#f5f5f0] transition-colors hover:border-[#c4a962] hover:text-[#c4a962]">
              <Phone className="h-4 w-4" /> 0407 051 043
            </a>
            <button onClick={() => onNav("#contact")} className="bg-[#f5f5f0] px-6 py-4 text-sm font-semibold uppercase tracking-wide text-[#050505] transition-colors hover:bg-[#c4a962]">
              Request a quote
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
